import { v4 as uuidv4 } from "uuid";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { AttributeClassification, AttributeLabel, AttributeType, RpServiceAttributeProperty, RpServiceAttributePropertyMap } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfigurationAttribute, RpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { isNullOrUndefined } from "@utils/object-util";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { getDefaultAttributeLabel } from "@utils/attribute-label-util";

export const formatRpServiceConfigurationAttributes = (
    attributes: Array<RpServiceConfigurationAttribute>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    autoSubmitFlagAttributeProperty: RpServiceAttributeProperty,
    doNotSaveToPDSAttributeProperty: RpServiceAttributeProperty,
    ignorePDSDataAttributeProperty: RpServiceAttributeProperty,
    isAsyncAttributeProperty: RpServiceAttributeProperty,
    optionalFlagAttributeProperty: RpServiceAttributeProperty,
    placeholderFlagAttributeProperty: RpServiceAttributeProperty,
    requiredForSelfServiceVerificationAttributeProperty: RpServiceAttributeProperty,
    userEditableFlagAttributeProperty: RpServiceAttributeProperty,
    userVisibleFlagAttributeProperty: RpServiceAttributeProperty,
    customToolTipAttributeProperty: RpServiceAttributeProperty,
    displayFullValueAttributeProperty: RpServiceAttributeProperty,
    axnuiContent: AXNUIContent
): Array<FormattedRpServiceConfigurationAttribute> => {

    const finalAttributes: Array<FormattedRpServiceConfigurationAttribute> = [];

    attributeTypes.forEach(attributeType => {
        const attributesWithAttributeId = attributes.filter(a => a.attributeTypeID === attributeType.id);

        if (attributesWithAttributeId.length > 0) {
            attributesWithAttributeId.forEach(attrWithAttrId => {
                const formattedAttrWithAttrId = formatRpServiceConfigurationAttribute(
                    attrWithAttrId,
                    attributeType,
                    attributeLabels,
                    attributeClassifications,
                    rpServiceAttributePropertyMaps,
                    autoSubmitFlagAttributeProperty,
                    doNotSaveToPDSAttributeProperty,
                    ignorePDSDataAttributeProperty,
                    isAsyncAttributeProperty,
                    optionalFlagAttributeProperty,
                    placeholderFlagAttributeProperty,
                    requiredForSelfServiceVerificationAttributeProperty,
                    userEditableFlagAttributeProperty,
                    userVisibleFlagAttributeProperty,
                    customToolTipAttributeProperty,
                    displayFullValueAttributeProperty,
                    axnuiContent
                );

                finalAttributes.push(formattedAttrWithAttrId);
            });
        } else {
            const formattedAttrWithAttrId = formatRpServiceConfigurationAttribute(
                null,
                attributeType,
                attributeLabels,
                attributeClassifications,
                rpServiceAttributePropertyMaps,
                autoSubmitFlagAttributeProperty,
                doNotSaveToPDSAttributeProperty,
                ignorePDSDataAttributeProperty,
                isAsyncAttributeProperty,
                optionalFlagAttributeProperty,
                placeholderFlagAttributeProperty,
                requiredForSelfServiceVerificationAttributeProperty,
                userEditableFlagAttributeProperty,
                userVisibleFlagAttributeProperty,
                customToolTipAttributeProperty,
                displayFullValueAttributeProperty,
                axnuiContent
            );

            finalAttributes.push(formattedAttrWithAttrId);
        }
    });

    return finalAttributes;
};

export const formatRpServiceConfigurationAttribute = (
    attribute: RpServiceConfigurationAttribute,
    attributeType: AttributeType,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    autoSubmitFlagAttributeProperty: RpServiceAttributeProperty,
    doNotSaveToPDSAttributeProperty: RpServiceAttributeProperty,
    ignorePDSDataAttributeProperty: RpServiceAttributeProperty,
    isAsyncAttributeProperty: RpServiceAttributeProperty,
    optionalFlagAttributeProperty: RpServiceAttributeProperty,
    placeholderFlagAttributeProperty: RpServiceAttributeProperty,
    requiredForSelfServiceVerificationAttributeProperty: RpServiceAttributeProperty,
    userEditableFlagAttributeProperty: RpServiceAttributeProperty,
    userVisibleFlagAttributeProperty: RpServiceAttributeProperty,
    customToolTipAttributeProperty: RpServiceAttributeProperty,
    displayFullValueAttributeProperty: RpServiceAttributeProperty,
    axnuiContent: AXNUIContent
): FormattedRpServiceConfigurationAttribute => {
    let attributeLabel = attributeLabels.find(al => al.id === attribute?.attributeLabelID);

    const existingAttributeLabels = attributeLabels.filter(al => al.attributeTypeID === attributeType?.id);
    if (!attributeLabel) {
        attributeLabel = getDefaultAttributeLabel(
            attributeType,
            existingAttributeLabels,
            axnuiContent
        );
    }

    const attributeClassification = attributeClassifications.find(ac => ac.id === attributeType.attributeClassificationID);

    let autoSubmitFlag = attribute?.autoSubmitFlag || "false";
    if (!attribute?.doNotSaveToPDS && autoSubmitFlagAttributeProperty) {
        const autoSubmitFlagAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === autoSubmitFlagAttributeProperty.id
        );
        if (autoSubmitFlagAttributePropertyMap && autoSubmitFlagAttributePropertyMap.defaultValue) {
            autoSubmitFlag = autoSubmitFlagAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let doNotSaveToPDS = attribute?.doNotSaveToPDS || "false";
    if (!attribute?.doNotSaveToPDS && doNotSaveToPDSAttributeProperty) {
        const doNotSaveToPDSAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === doNotSaveToPDSAttributeProperty.id
        );
        if (doNotSaveToPDSAttributePropertyMap?.defaultValue) {
            doNotSaveToPDS = doNotSaveToPDSAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let ignorePDSData = attribute?.ignorePDSData || "false";
    if (!attribute?.ignorePDSData && ignorePDSDataAttributeProperty) {
        const ignorePDSDataAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === ignorePDSDataAttributeProperty.id
        );
        if (ignorePDSDataAttributePropertyMap?.defaultValue) {
            ignorePDSData = ignorePDSDataAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let isAsync = attribute?.isAsync || "false";
    if (!attribute?.isAsync && isAsyncAttributeProperty) {
        const isAsyncAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === isAsyncAttributeProperty.id
        );
        if (isAsyncAttributePropertyMap?.defaultValue) {
            isAsync = isAsyncAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let optionalFlag = attribute?.optionalFlag || "false";
    if (!attribute?.optionalFlag && optionalFlagAttributeProperty) {
        const optionalFlagAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === optionalFlagAttributeProperty.id
        );
        if (optionalFlagAttributePropertyMap && optionalFlagAttributePropertyMap.defaultValue) {
            optionalFlag = optionalFlagAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let placeholderFlag = attribute?.placeholderFlag || "false";
    if (!attribute?.placeholderFlag && placeholderFlagAttributeProperty) {
        const placeholderFlagAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === placeholderFlagAttributeProperty.id
        );
        if (placeholderFlagAttributePropertyMap?.defaultValue) {
            placeholderFlag = placeholderFlagAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let requiredForSelfServiceVerification = attribute?.requiredForSelfServiceVerification || "false";
    if (!attribute?.requiredForSelfServiceVerification && requiredForSelfServiceVerificationAttributeProperty) {
        const requiredForSelfServiceVerificationAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === requiredForSelfServiceVerificationAttributeProperty.id
        );
        if (requiredForSelfServiceVerificationAttributePropertyMap && requiredForSelfServiceVerificationAttributePropertyMap.defaultValue) {
            requiredForSelfServiceVerification = requiredForSelfServiceVerificationAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let userEditableFlag = attribute?.userEditableFlag || "true";
    if (!attribute?.userEditableFlag && userEditableFlagAttributeProperty) {
        const userEditableFlagAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === userEditableFlagAttributeProperty.id
        );
        if (userEditableFlagAttributePropertyMap?.defaultValue) {
            userEditableFlag = userEditableFlagAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let userVisibleFlag = attribute?.userVisibleFlag || "true";
    if (!attribute?.userVisibleFlag && userVisibleFlagAttributeProperty) {
        const userVisibleFlagAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === userVisibleFlagAttributeProperty.id
        );
        if (userVisibleFlagAttributePropertyMap?.defaultValue) {
            userVisibleFlag = userVisibleFlagAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let displayFullValue = attribute?.displayFullValue || "false";
    if (!attribute?.displayFullValue && displayFullValueAttributeProperty) {
        const displayFullValueAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === displayFullValueAttributeProperty.id  
        );
        if (displayFullValueAttributePropertyMap?.defaultValue) {
            displayFullValue = displayFullValueAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    let customToolTip = attribute?.customToolTip || null;
    if (isNullOrUndefined(attribute?.customToolTip) && customToolTipAttributeProperty) {
        const customToolTipAttributePropertyMap = rpServiceAttributePropertyMaps.find(
            apm => apm.attributeTypeID === attributeType.id && apm.rpServiceAttributePropertyID === customToolTipAttributeProperty.id
        );
        if (customToolTipAttributePropertyMap?.defaultValue) {
            customToolTip = customToolTipAttributePropertyMap.defaultValue.toLowerCase();
        }
    }

    return {
        attributeLabel: attributeLabel,
        attributeLabels: existingAttributeLabels,
        attributeType: attributeType,
        autoSubmitFlag: autoSubmitFlag,
        customError: attribute?.customError,
        customPINContactURL: attribute?.customPINContactURL,
        customTemplateName: attribute?.customTemplateName,
        customToolTip: customToolTip,
        dependentAttributeID: attribute?.dependentAttributeID,
        displayFullValue: displayFullValue,
        doNotSaveToPDS: doNotSaveToPDS,
        id: attribute?.id,
        ignorePDSData: ignorePDSData,
        isAsync: isAsync,
        optionalFlag: optionalFlag,
        order: attribute?.order ? parseInt(attribute.order) : null,
        placeholderFlag: placeholderFlag,
        requiredForSelfServiceVerification: requiredForSelfServiceVerification,
        rpServiceID: attribute?.rpServiceID,
        status: attribute?.status || EntityStatusTypes.INACTIVE,
        userEditableFlag: userEditableFlag,
        userVisibleFlag: userVisibleFlag,
        name: attributeType.name,
        display: attributeType.display,
        attributeClassification: attributeClassification,
        templateName: attributeType.templateName,
        verifiable: attributeType.verifiable,
        addedToService: !!attribute,
        populateCredentialFromThisAttribute: attribute?.populateCredentialFromThisAttribute || 'false',
        uuid: uuidv4()
    };
};